import type { TTSMode } from './ttsTypes';

/** Qwen3-TTS synthesis config */
export interface Qwen3SynthesisParams {
  text: string;
  language: string;
  speaker: string;
  style?: string;
  mode: TTSMode;
  voiceDescription?: string;
  refAudioBlob?: Blob;
  refFileName?: string;
  refText?: string;
  outputFormat: 'wav' | 'mp3';
  serverUrl: string;
}

/** Synthesize audio via Qwen3-TTS server */
export async function synthesizeQwen3(params: Qwen3SynthesisParams): Promise<Blob> {
  const formData = new FormData();
  formData.append('text', params.text);
  formData.append('language', params.language);
  formData.append('format', params.outputFormat);

  let endpoint = '/synthesize';

  if (params.mode === 'synthesize') {
    formData.append('speaker', params.speaker);
    if (params.style) formData.append('style', params.style);
  } else if (params.mode === 'clone') {
    endpoint = '/clone';
    if (params.refAudioBlob) {
      formData.append('ref_audio', params.refAudioBlob, params.refFileName || 'ref.wav');
    }
    if (params.refText) {
      formData.append('ref_text', params.refText);
    }
    if (params.style) formData.append('style', params.style);
  } else if (params.mode === 'design') {
    endpoint = '/design';
    if (params.voiceDescription) {
      formData.append('voice_description', params.voiceDescription);
    }
    if (params.style) formData.append('style', params.style);
  }

  const response = await fetch(`${params.serverUrl}${endpoint}`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Qwen3-TTS: Failed to generate audio');
  }

  return response.blob();
}

/** Synthesize audio via Bert-VITS2 server (proxied through Next.js API) */
export async function synthesizeBertVits2(params: {
  text: string;
  modelId: string;
  speakerName: string;
  style: string;
}): Promise<Blob> {
  const response = await fetch('/api/voice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text: params.text,
      model_id: params.modelId,
      speaker_name: params.speakerName,
      style: params.style,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Bert-VITS2: HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.details || errorData.error || errorMessage;
    } catch {
      const errorText = await response.text().catch(() => '');
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  return response.blob();
}

/** Synthesize audio via Irodori-TTS server (proxied through Next.js API) */
export interface IrodoriSynthesisParams {
  text: string;
  voice: string;
  speed?: number;
  /** 既定は wav。タイムライン素材には mp3 を使う (容量が 1/10 以下になる)。 */
  responseFormat?: 'wav' | 'mp3';
  numSteps?: number;
  cfgScaleText?: number;
  cfgScaleSpeaker?: number;
  seed?: number | null;
  /**
   * VoiceDesign の指示文 (日本語)。caption 対応の checkpoint (v4 系) でのみ効く。
   * 参照音声と矛盾すると音が崩れるので、感情・スタイル・環境だけを書き、
   * 声の基本的な特徴は参照音声に任せる (モデルカードの注意書き)。
   */
  caption?: string;
  cfgScaleCaption?: number;
  tScheduleMode?: 'linear' | 'sway';
  swayCoeff?: number;
  /**
   * 参照音声として使う秒数の上限。
   *
   * v4 系は既定で **120 秒** まで使うが、参照の前処理 (prepare_reference) は
   * 長さに対して超線形に効く。実測では 325 秒の参照で 13.5 秒かかり、合成時間の
   * 87% を占めた。30 秒に制限すると同じ文が 11.4 秒 → 1.5 秒になった。
   * 対話のようにすぐ返したい用途では必ず指定する。
   */
  maxRefSeconds?: number;
}

export interface IrodoriSynthesisResult {
  blob: Blob;
  /** サーバが実際に使った seed (X-Irodori-Seed header) — random seed 時の値確認用 */
  usedSeed: number | null;
}

export async function synthesizeIrodori(params: IrodoriSynthesisParams): Promise<Blob> {
  const result = await synthesizeIrodoriWithMeta(params);
  return result.blob;
}

export async function synthesizeIrodoriWithMeta(params: IrodoriSynthesisParams): Promise<IrodoriSynthesisResult> {
  const response = await fetch('/api/voice/irodori', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text: params.text,
      voice: params.voice,
      speed: params.speed ?? 1.0,
      response_format: params.responseFormat ?? 'wav',
      num_steps: params.numSteps,
      cfg_scale_text: params.cfgScaleText,
      cfg_scale_speaker: params.cfgScaleSpeaker,
      seed: params.seed ?? undefined,
      caption: params.caption,
      cfg_scale_caption: params.cfgScaleCaption,
      t_schedule_mode: params.tScheduleMode,
      sway_coeff: params.swayCoeff,
      max_ref_seconds: params.maxRefSeconds,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Irodori-TTS: HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.details || errorData.error || errorMessage;
    } catch {
      const errorText = await response.text().catch(() => '');
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  const seedHeader = response.headers.get('X-Irodori-Seed');
  const usedSeed = seedHeader && /^-?\d+$/.test(seedHeader) ? Number(seedHeader) : null;
  const blob = await response.blob();
  return { blob, usedSeed };
}

/** Upload a voice file to Irodori-TTS server */
export async function uploadIrodoriVoice(params: { voiceId: string; file: Blob; fileName?: string }): Promise<void> {
  const formData = new FormData();
  formData.append('voice_id', params.voiceId);
  formData.append('file', params.file, params.fileName ?? `${params.voiceId}.wav`);
  const response = await fetch('/api/voice/irodori-voices', {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Irodori-TTS voice upload failed: HTTP ${response.status}${errorText ? ` — ${errorText}` : ''}`);
  }
}

/** Delete a voice from Irodori-TTS server */
export async function deleteIrodoriVoice(voiceId: string): Promise<void> {
  const response = await fetch(`/api/voice/irodori-voices/${encodeURIComponent(voiceId)}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Irodori-TTS voice delete failed: HTTP ${response.status}${errorText ? ` — ${errorText}` : ''}`);
  }
}

/**
 * 参照音声の名前を変える (実ファイルを rename する)。
 * 保存済みのキャラプリセット / サウンドノベルのセッションも同時に追従する。
 */
export async function renameIrodoriVoice(
  from: string,
  to: string,
): Promise<{ presets: number; sessions: number } | null> {
  const response = await fetch('/api/voice/irodori-voices/rename', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to }),
  });
  const data = (await response.json().catch(() => null)) as
    | { success?: boolean; message?: string; retargeted?: { presets: number; sessions: number } | null }
    | null;
  if (!response.ok || !data?.success) {
    throw new Error(data?.message ?? `名前を変えられませんでした (HTTP ${response.status})`);
  }
  return data.retargeted ?? null;
}

/** Fetch available voice IDs from Irodori-TTS server (proxied) */
export async function fetchIrodoriVoices(): Promise<string[]> {
  try {
    const response = await fetch('/api/voice/irodori-voices');
    if (!response.ok) return ['none'];
    const data = await response.json();
    const voices = Array.isArray(data?.voices) ? data.voices : [];
    // 必ず 'none' を先頭に (text-only synthesis 用)
    const filtered = voices.filter((v: unknown): v is string => typeof v === 'string' && v !== 'none');
    return ['none', ...filtered];
  } catch {
    return ['none'];
  }
}

// ============ Preset Texts (editable library) ============

export interface TtsPresetText {
  id: number;
  label: string;
  text: string;
  category: string;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

/** Fetch all preset texts from SQLite */
export async function fetchPresetTexts(): Promise<TtsPresetText[]> {
  const res = await fetch('/api/tts/preset-texts');
  if (!res.ok) throw new Error('Failed to fetch preset texts');
  const data = await res.json();
  return data.success && Array.isArray(data.presets) ? data.presets : [];
}

/** Create a new preset text */
export async function createPresetText(input: {
  label: string;
  text: string;
  category: string;
}): Promise<TtsPresetText> {
  const res = await fetch('/api/tts/preset-texts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create preset text');
  return data.preset;
}

/** Update an existing preset text */
export async function updatePresetText(
  id: number,
  updates: { label?: string; text?: string; category?: string },
): Promise<TtsPresetText> {
  const res = await fetch('/api/tts/preset-texts', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...updates }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to update preset text');
  return data.preset;
}

/** Delete a preset text */
export async function deletePresetText(id: number): Promise<void> {
  const res = await fetch(`/api/tts/preset-texts?id=${id}`, { method: 'DELETE' });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to delete preset text');
}
